// src/Checklist/ChecklistPanel.jsx
import React, { useEffect, useState } from "react";
import Select from "react-select";
import { api } from "../api";
import ChecklistFillModal from "./ChecklistFillModal";
import "./ChecklistPanel.css";

const ChecklistPanel = ({ user }) => {
  const [templates, setTemplates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [pendingList, setPendingList] = useState([]);
  const [submittedList, setSubmittedList] = useState([]);

  const [templateId, setTemplateId] = useState("");
  const [project, setProject] = useState(null);
  const [assignTo, setAssignTo] = useState(null);

  const [modalConfig, setModalConfig] = useState(null);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [busy, setBusy] = useState(false);

  const authHeader = {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  };

  // Name of the logged-in user, used to pre-fill "Prepared By"
  const userName = String(user?.name || "").trim();

  // All user names, used for the "Prepared By" / "Checked By" dropdowns
  const userNames = [
    ...new Set(users.map((u) => String(u.name || "").trim()).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b));

  const flash = (text, type = "ok") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 4000);
  };

  // "Pending" = not filled yet, OR filled but not attached to a timesheet entry yet.
  // "Submitted" = filled and attached to a timesheet entry.
  const loadLists = async () => {
    try {
      const [p, s] = await Promise.all([
        api.get("/checklists/my?status=pending", authHeader),
        api.get("/checklists/my?status=submitted", authHeader),
      ]);
      setPendingList(Array.isArray(p.data) ? p.data : []);
      setSubmittedList(
        (Array.isArray(s.data) ? s.data : []).filter((a) => a.logId),
      );
    } catch (e) {
      console.warn("Failed to load my checklists", e);
      setPendingList([]);
      setSubmittedList([]);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const [t, p, u] = await Promise.all([
          api.get("/checklists/templates", authHeader),
          api.get("/projects", authHeader),
          api.get("/users", authHeader),
        ]);
        setTemplates(Array.isArray(t.data) ? t.data : []);
        setProjects(Array.isArray(p.data) ? p.data : []);
        const ulist = Array.isArray(u.data) ? u.data : u.data?.users || [];
        setUsers(ulist);
      } catch (e) {
        console.warn("Checklist panel load failed", e);
      }
      loadLists();
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedTemplate = templates.find((t) => t._id === templateId);

  // each project option also carries the project address
  const projectOptions = projects.map((p) => ({
    label: p.name,
    value: p._id,
    address: String(p.clientAddress || "").trim(),
  }));

  const userOptions = users.map((u) => ({
    label: u.name || u.email || u._id,
    value: u._id,
  }));

  const needTemplateAndProject = () => {
    if (!templateId || !project) {
      flash("Please select a checklist and a project.", "err");
      return false;
    }
    return true;
  };

  const handleFillNow = () => {
    if (!needTemplateAndProject()) return;
    setModalConfig({
      mode: "self",
      title: selectedTemplate.title,
      htmlPath: selectedTemplate.htmlPath,
      templateId,
      projectId: project.value,
      projectName: project.label,
      data: {},
      readOnly: false,
      defaults: {
        projectName: project.label,
        projectAddress: project.address,
        preparedBy: userName,
      },
      users: userNames,
    });
  };

  const handleAssign = async () => {
    if (!needTemplateAndProject()) return;
    if (!assignTo) {
      flash("Please select a user to assign.", "err");
      return;
    }
    try {
      setBusy(true);
      await api.post(
        "/checklists/assign",
        {
          templateId,
          projectId: project.value,
          assignedTo: assignTo.value,
        },
        authHeader,
      );
      flash(`Checklist assigned to ${assignTo.label}.`);
      setAssignTo(null);
      loadLists();
    } catch (err) {
      flash(
        err?.response?.data?.message || "Could not assign the checklist.",
        "err",
      );
    } finally {
      setBusy(false);
    }
  };

  const openAssignment = async (a, readOnly) => {
    const tpl = templates.find((t) => t._id === String(a.templateId));
    if (!tpl || !tpl.htmlPath) {
      flash("This checklist template is not available.", "err");
      return;
    }

    // find the project to get its address
    const projectId = String(a.projectId?._id || a.projectId || "");
    const proj = projects.find((p) => String(p._id) === projectId);

    try {
      // load fresh data for this record
      const res = await api.get(`/checklists/${a._id}`, authHeader);
      setModalConfig({
        mode: "assignment",
        title: a.templateTitle,
        htmlPath: tpl.htmlPath,
        assignmentId: a._id,
        projectName: a.projectName,
        data: res.data?.data || {},
        readOnly,
        defaults: {
          projectName: a.projectName,
          projectAddress: String(proj?.clientAddress || "").trim(),
          preparedBy: userName,
        },
        users: userNames,
      });
    } catch (err) {
      flash("Could not open the checklist.", "err");
    }
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "");

  return (
    <div className="card cp-card">
      <h3 className="cp-heading">📋 Checklists</h3>

      {message.text && (
        <div
          className={`cp-msg ${message.type === "err" ? "cp-err" : "cp-ok"}`}
        >
          {message.text}
        </div>
      )}

      <div className="cp-form">
        <label className="cp-label">Checklist</label>
        <select
          className="cp-select"
          value={templateId}
          onChange={(e) => setTemplateId(e.target.value)}
        >
          <option value="">Select checklist...</option>
          {templates.map((t) => (
            <option key={t._id} value={t._id}>
              {t.title}
            </option>
          ))}
        </select>

        <label className="cp-label">Project</label>
        <Select
          options={projectOptions}
          value={project}
          onChange={setProject}
          placeholder="Select project..."
        />

        <div className="cp-actions">
          <button type="button" className="cp-btn" onClick={handleFillNow}>
            Fill Now
          </button>
        </div>

        <label className="cp-label">Or assign to (without filling)</label>
        <Select
          options={userOptions}
          value={assignTo}
          onChange={setAssignTo}
          placeholder="Select user..."
        />

        <div className="cp-actions">
          <button
            type="button"
            className="cp-btn cp-btn-alt"
            onClick={handleAssign}
            disabled={busy}
          >
            {busy ? "Assigning..." : "Assign"}
          </button>
        </div>
      </div>

      <h4 className="cp-sub">My Pending Checklists</h4>
      {pendingList.length === 0 ? (
        <div className="cp-empty">No pending checklists.</div>
      ) : (
        pendingList.map((a) => {
          const filled = a.status === "submitted";

          return (
            <div key={a._id} className="cp-item">
              <div className="cp-item-main">
                <div className="cp-item-title">
                  {a.templateTitle}
                  {filled && <span className="cp-badge">Filled</span>}
                </div>
                <div className="cp-item-meta">
                  {a.projectName} · from {a.assignedBy?.name || "—"} ·{" "}
                  {fmtDate(a.createdAt)}
                </div>
                {filled && (
                  <div className="cp-item-hint">
                    Tick "Attach to this entry" in My Pending Checklists on the
                    timesheet page and submit the entry. It stays here until
                    then.
                  </div>
                )}
              </div>
              <button
                type="button"
                className={`cp-btn cp-btn-small ${filled ? "cp-btn-alt" : ""}`}
                onClick={() => openAssignment(a, filled)}
              >
                {filled ? "View" : "Open"}
              </button>
            </div>
          );
        })
      )}

      {submittedList.length > 0 && (
        <>
          <h4 className="cp-sub">Recently Submitted</h4>
          {submittedList.slice(0, 5).map((a) => (
            <div key={a._id} className="cp-item">
              <div className="cp-item-main">
                <div className="cp-item-title">{a.templateTitle}</div>
                <div className="cp-item-meta">
                  {a.projectName} · {fmtDate(a.submittedAt)}
                </div>
              </div>
              <button
                type="button"
                className="cp-btn cp-btn-small cp-btn-alt"
                onClick={() => openAssignment(a, true)}
              >
                View
              </button>
            </div>
          ))}
        </>
      )}

      {modalConfig && (
        <ChecklistFillModal
          config={modalConfig}
          onClose={() => setModalConfig(null)}
          onSaved={() => loadLists()}
        />
      )}
    </div>
  );
};

export default ChecklistPanel;
